import React, { useState } from "react";
import { useSelector } from "react-redux";
import Typography from "../../../Utility/Typography";
import Pagination from "../../../Utility/Pagination/Pagination";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import { selectExportsHistoryData } from "../../../Redux/Slices/exportsSlice";
import { formatExportStatusLabel } from "../../../utils/exportHistoryNormalize";
import { downloadBillingExportFile } from "../../../utils/downloadBillingExportFile";
import { showError } from "../../../utils/toast";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const ExportHistory = () => {
  const historyData = useSelector(selectExportsHistoryData) || [];
  const [downloadingId, setDownloadingId] = useState(null);
  /* ===================== SORTING ===================== */
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(historyData, {
    defaultSortKey: "date",
    defaultSortDirection: "desc",
  });

  /* ===================== PAGINATION ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleHistoryDownload = async (row) => {
    if (!row?.canDownload || !row?.id) return;
    setDownloadingId(row.id);
    try {
      const ext = (row.format || "CSV").toLowerCase();
      const fallback = row.fileName?.trim() || `export.${ext === "xlsx" ? "xlsx" : "csv"}`;
      await downloadBillingExportFile(row.id, fallback);
    } catch (e) {
      showError(getApiErrorMessage(e, "Download failed"));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="w-full mt-8">
      {/* ===================== TITLE ===================== */}
      <Typography variant="h4" className="mb-3">
        Export History
      </Typography>

      {/* ===================== TABLE ===================== */}
      <div className="relative flex flex-col bg-surface-primary shadow-sm">
        <div className="flex-1 p-2">
          <div className="overflow-x-auto">
            <table className="w-full mb-0 clients-table">
              {/* ===================== TABLE HEADER ===================== */}
              <thead className="bg-surface-secondary">
                <tr>
                  <SortableHeader
                    columnKey="date"
                    label="Export Date"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="exportedBy"
                    label="Exported By"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="filters"
                    label="Filters"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="format"
                    label="Format"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="status"
                    label="Status"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="rowCount"
                    label="Rows"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    columnKey="download"
                    label="Download"
                    sortable={false}
                    className="text-right"
                  />
                </tr>
              </thead>

              {/* ===================== TABLE BODY ===================== */}
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-6">
                      <Typography variant="span1" className="text-text-secondary">
                        No export history found
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Typography variant="span1" className="font-medium">
                          {row.date}
                        </Typography>
                      </td>

                      <td>
                        <Typography variant="span1">{row.exportedBy}</Typography>
                      </td>

                      <td>
                        <Typography variant="span1" className="text-text-secondary">
                          {row.filters}
                        </Typography>
                      </td>

                      <td>
                        <Typography variant="span1">{row.format || "—"}</Typography>
                      </td>

                      <td>
                        <Typography
                          variant="span1"
                          className={
                            row.status === "success" ? "text-text-primary" : "text-text-secondary"
                          }
                          title={row.errorMessage || undefined}
                        >
                          {formatExportStatusLabel(row.status)}
                        </Typography>
                      </td>

                      <td>
                        <Typography variant="span1">{row.rowCount}</Typography>
                      </td>

                      <td className="text-end align-middle">
                        {row.canDownload ? (
                          <Typography
                            variant="span1"
                            component="button"
                            type="button"
                            disabled={downloadingId === row.id}
                            className="cursor-pointer underline hover:opacity-80 focus:outline-none text-text-link bg-transparent border-0 p-0 font-inherit disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => void handleHistoryDownload(row)}
                          >
                            {downloadingId === row.id ? "Downloading…" : "Download"}
                          </Typography>
                        ) : (
                          <Typography
                            variant="span1"
                            className="text-text-secondary"
                            title={row.errorMessage || "Export file not available"}
                          >
                            —
                          </Typography>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===================== PAGINATION ===================== */}
        <div className="px-4 py-3 border-t bg-surface-secondary">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ExportHistory;
